import { useEffect, useRef, useState } from "react";
import type { CropRect } from "../processing/types";

// Größtes Rechteck mit gegebenem Seitenverhältnis, das ins Bild passt (in Pixeln).
function baseCrop(imgW: number, imgH: number, aspect: number): { w: number; h: number } {
  if (imgW / imgH > aspect) {
    const h = imgH;
    return { w: h * aspect, h };
  }
  const w = imgW;
  return { w, h: w / aspect };
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

interface Props {
  bitmap: ImageBitmap;
  aspect: number;
  label?: string;
  boxW: number;
  boxH: number;
  onCrop: (c: CropRect) => void;
  // Optionaler Start-Ausschnitt (normalisiert), um eine gespeicherte Anpassung
  // wiederherzustellen. Ohne Angabe: mittig, Zoom 1.
  initialCrop?: CropRect;
  disabled?: boolean;
}

// Interaktive 4:3/3:4-Zuschnittsvorschau: die Canvas zeigt exakt den späteren
// Ausschnitt; Ziehen verschiebt, der Regler zoomt. Ausgabe ist normalisiert (0..1).
export function CropFrame({ bitmap, aspect, label, boxW, boxH, onCrop, initialCrop, disabled }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const base = baseCrop(bitmap.width, bitmap.height, aspect);

  const derive = () => {
    if (initialCrop) {
      const cropWpx = initialCrop.w * bitmap.width;
      const z = clamp(base.w / cropWpx, 1, 3);
      return {
        zoom: z,
        cx: (initialCrop.x + initialCrop.w / 2) * bitmap.width,
        cy: (initialCrop.y + initialCrop.h / 2) * bitmap.height,
      };
    }
    return { zoom: 1, cx: bitmap.width / 2, cy: bitmap.height / 2 };
  };

  const init = derive();
  const [zoom, setZoom] = useState(init.zoom);
  const [centerPx, setCenterPx] = useState({ x: init.cx, y: init.cy });
  const dragRef = useRef<{ px: number; py: number; cx: number; cy: number } | null>(null);

  // Bei Bild-/Formatwechsel neu initialisieren.
  useEffect(() => {
    const d = derive();
    setZoom(d.zoom);
    setCenterPx({ x: d.cx, y: d.cy });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitmap, aspect]);

  const cropW = base.w / zoom;
  const cropH = base.h / zoom;
  const cx = clamp(centerPx.x, cropW / 2, bitmap.width - cropW / 2);
  const cy = clamp(centerPx.y, cropH / 2, bitmap.height - cropH / 2);
  const cropX = cx - cropW / 2;
  const cropY = cy - cropH / 2;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, boxW, boxH);
    ctx.drawImage(bitmap, cropX, cropY, cropW, cropH, 0, 0, boxW, boxH);
  }, [bitmap, cropX, cropY, cropW, cropH, boxW, boxH]);

  // Ausschnitt normalisiert nach oben melden.
  useEffect(() => {
    onCrop({
      x: cropX / bitmap.width,
      y: cropY / bitmap.height,
      w: cropW / bitmap.width,
      h: cropH / bitmap.height,
    });
  }, [cropX, cropY, cropW, cropH, bitmap, onCrop]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, cx, cy };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const factorX = cropW / boxW;
    const factorY = cropH / boxH;
    setCenterPx({ x: d.cx - (e.clientX - d.px) * factorX, y: d.cy - (e.clientY - d.py) * factorY });
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  return (
    <div>
      {label && <div className="text-xs font-medium text-kampmeyer-neutral-600 mb-1">{label}</div>}
      <canvas
        ref={canvasRef}
        width={boxW}
        height={boxH}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={`rounded border border-kampmeyer-neutral-300 touch-none ${disabled ? "" : "cursor-move"}`}
        style={{ width: boxW, height: boxH }}
      />
      <input
        type="range"
        min={1}
        max={3}
        step={0.01}
        value={zoom}
        disabled={disabled}
        onChange={(e) => setZoom(Number(e.target.value))}
        className="w-full mt-2"
        style={{ maxWidth: boxW }}
        aria-label={`Zoom ${label ?? ""}`}
      />
    </div>
  );
}
